import React, { useEffect, useState } from "react";

import classnames from "classnames";
import { IndividualLifeQuoteType } from "IndividualLife/types";
import formatCurrency from "Utils/formatCurrency";

import { Body1, Body2, SubBody } from "Cobalt/BodyText";
import Button from "Cobalt/Button";
import Header from "Cobalt/Header";
import { Modal, ModalBody, ModalTitle, ModalHeader, ModalFooter } from "Cobalt/Modal";
import { Popover } from "Cobalt/Popover";
import { Range } from "Cobalt/Range";
import { Row, Col } from "Common/Grid";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { windowSizeAtLeast } from "Common/Responsive";
import SIOverlay from "Common/SIOverlay";

import { useGetIndividualLifeQuotesQuery, useGetIndividualLifeURLMutation } from "./api";

interface FormattedQuotesType {
  coverages: number[];
  terms: number[];
}
interface IndividualLifeSlidersTypes {
  coverageIndex: number;
  setCoverageIndex: Function;
  termIndex: number;
  setTermIndex: Function;
  formattedQuotes: FormattedQuotesType;
  selectedQuote: IndividualLifeQuoteType;
}
const IndividualLifeSliders = ({ coverageIndex, setCoverageIndex, termIndex, setTermIndex, formattedQuotes, selectedQuote }: IndividualLifeSlidersTypes) => (
  <Row my={6} pb={6}>
    <Col xs={12} sm={4} px={6}>
      <SIOverlay
        placement="right"
        overlayContent={
          <Popover showBackdrop={false} id="coverage_amount">
            Rule of thumb: 5-10x your annual income
          </Popover>
        }
        delay={200}
        overlayTarget={
          <Body2 className="plan-term-tooltip font-semibold text-gray">
            Coverage Amount
            <Icon icon="question-circle" size="sm" ml={3} />
          </Body2>
        }
      />
      <Header level={5} weight="bold">
        {formatCurrency(formattedQuotes.coverages[coverageIndex])}
      </Header>
      <Range
        input={{
          id: "coverage-range",
          value: coverageIndex,
        }}
        onChange={(newValue) => {
          setCoverageIndex(newValue);
        }}
        min={0}
        max={formattedQuotes.coverages.length - 1}
        hideLabels
      />
    </Col>
    <Col xs={12} sm={4} px={6}>
      <Body2 className="font-semibold text-gray">Term</Body2>
      <Div className="relative">
        <Header level={5} weight="bold" className="inline-block">
          {formattedQuotes.terms[termIndex]}
        </Header>
        <Body1 pl={2} className="inline-block">
          Years
        </Body1>
      </Div>
      <Range
        input={{
          id: "term-range",
          value: termIndex,
        }}
        onChange={(newValue) => {
          setTermIndex(newValue);
        }}
        min={0}
        max={formattedQuotes.terms.length - 1}
        hideLabels
      />
    </Col>
    <Col xs={12} sm={4} px={6}>
      <Body2 className="font-semibold text-gray">Your Estimated Price</Body2>
      <Div className="relative">
        <Header level={5} weight="bold" className="inline-block">
          {formatCurrency(selectedQuote?.daily_price, 2)}
        </Header>
        <Body1 pl={2} className="inline-block">
          /day
        </Body1>
      </Div>
    </Col>
  </Row>
);

export const IndividualLifeModal = ({ isShowing, onHide, employeeForm }: { isShowing: boolean; onHide: Function; employeeForm: any }) => {
  const [getIndividualLifeURL] = useGetIndividualLifeURLMutation();
  const { data, isLoading: isLoadingQuotes = false } = useGetIndividualLifeQuotesQuery();
  const lifeQuotes = data?.quotes || [];
  const employee = employeeForm;

  const [formattedQuotes, setFormattedQuotes] = useState<FormattedQuotesType>({
    coverages: [],
    terms: [],
  });
  const [coverageIndex, setCoverageIndex] = useState<number>(0);
  const [termIndex, setTermIndex] = useState<number>(0);
  const [tryingApply, setTyringApply] = useState<boolean>(false);
  useEffect(() => {
    if (!isLoadingQuotes && lifeQuotes.length > 0) {
      const coverages = [];
      const terms = [];
      lifeQuotes.forEach((quote) => {
        if (!coverages.includes(quote.coverage)) {
          coverages.push(quote.coverage);
        }
        if (coverages[coverageIndex] === quote.coverage && !terms.includes(quote.term)) {
          terms.push(quote.term);
        }
      });
      if (termIndex > terms.length - 1) setTermIndex(0);

      coverages.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      terms.sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
      setFormattedQuotes({ coverages, terms });
    }
  }, [lifeQuotes, coverageIndex]);

  const selectedQuote = lifeQuotes.find(
    (quote) => quote.coverage === formattedQuotes.coverages[coverageIndex] && quote.term === formattedQuotes.terms[termIndex],
  );

  const navToLadder = async () => {
    setTyringApply(true);
    const { data: urlData } = await getIndividualLifeURL(selectedQuote);
    if (urlData.url) {
      window.open(urlData.url, "_blank").focus();
    } else {
      setTyringApply(false);
    }
  };

  return (
    <Modal
      className={classnames({
        "modal-fullscreen": !windowSizeAtLeast("md"),
      })}
      show={isShowing}
      onHide={onHide}
    >
      <ModalHeader closeButton>
        <ModalTitle className="font-bold">Individual Term Life Insurance</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Body2 pb={4} px={4} className="text-gray">
          Apply now and get an instant decision through our partner Ladder- no medical exam required for coverage up to $3M for most products. There is no cost
          to apply and you can complete it in minutes by adding health and lifestyle information (such as, whether or not you are a smoker).
        </Body2>
        <div className="flex justify-start">
          <Body2 px={4} className="font-semibold capitalize">
            {employee?.age && <>Age: {employee?.age} </>}
          </Body2>
          <Body2 px={4} className="font-semibold capitalize">
            {employee?.gender && <>Gender: {employee?.gender} </>}
          </Body2>
          <Body2 px={4} className="font-semibold capitalize">
            {employee?.zipCode && <>Zip Code: {employee?.zipCode} </>}
          </Body2>
        </div>
        <IndividualLifeSliders
          coverageIndex={coverageIndex}
          setCoverageIndex={setCoverageIndex}
          termIndex={termIndex}
          setTermIndex={setTermIndex}
          formattedQuotes={formattedQuotes}
          selectedQuote={selectedQuote}
        />
        <SubBody px={4} pt={6} mt={6} color="muted">
          Ladder Insurance Services, LLC (CA license # OK22568; AR license # 3000140372) distributes term life insurance products issued by multiple insurers –
          for further details see ladderlife.com. All insurance products are governed by the terms set forth in the applicable insurance policy. Each insurer
          has financial responsibility for its own products.
          <br />
          <br />
          Please note that the employer is providing the link for your convenience and that this is a voluntary life insurance product that is not being offered
          as an employer endorsed or sponsored plan.
        </SubBody>
      </ModalBody>
      <ModalFooter>
        <Button disabled={!!tryingApply} onClick={navToLadder}>
          {tryingApply && <Icon icon="spinner" spin="pulse" />}
          {!tryingApply && (
            <>
              Get Started <Icon icon="external-link-alt" flavor="solid" />
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
